import {shallowRef} from "vue";
import {asFinishedPeriod, KanbanPeriods} from "@/utils/finishedPeriod";
import {VisibleValues} from "@/utils/kanbanGrouping";
import {Access} from "@/types/Access";

type StoredUser = {
    /**
     * O nome, quando há.
     *
     * Opcional porque o servidor parou de inventá-lo: ele respondia `name = email` em memória, e
     * o avatar de todo mundo mostrava a inicial do endereço. Quem decide como desenhar quem não
     * tem nome é a tela, e ela desenha o e-mail.
     */
    name?: string
    /** A foto do perfil, que vem do `photoURL` do Firebase pelo `auth/sync`. */
    photo?: string
    email: string
    /** O `@` da conta, fixo — ver o `Person`. Vem do `auth/sync`. */
    alias?: string
    /**
     * A admissão desta conta no lunedi, que vem na resposta do `auth/sync`.
     *
     * É o único sinal de aplicação que o cliente tem — papel é por ambiente, e vive no
     * `workspace`. Opcional porque um servidor que ainda não o mandasse deixaria a tela
     * desenhando de menos, que é o lado seguro de errar.
     */
    access?: Access
    /**
     * O idioma preferido da conta, quando a pessoa escolheu um no perfil — ver `utils/language`.
     * Guardado aqui também para a partida já abrir nele, antes de o `auth/sync` responder.
     */
    language?: string
}

//Reexportado: o tipo desceu para junto do prazo, que é do que ele fala, mas meia dúzia de
//telas o importam daqui desde antes.
export type {KanbanPeriods}

/**
 * As duas visões de um relacionamento DENTRO da ficha de um item — as abas do bloco.
 *
 * Valores próprios, e não os nomes de rota da coleção: aqui não se navega, alterna-se o que o
 * bloco desenha. São as mesmas duas palavras na tela, e coisas diferentes por baixo.
 */
export type RelationshipView = 'list' | 'kanban'

const RELATIONSHIP_VIEWS: RelationshipView[] = ['list', 'kanban']

/** Os nomes de rota das duas visões de uma coleção. */
export type CollectionView = 'list-instances' | 'kanban'

const COLLECTION_VIEWS: CollectionView[] = ['list-instances', 'kanban']

function collectionViewKey(systemAlias: string, ideaAlias: string) {
    return `collection-view:${systemAlias}/${ideaAlias}`
}

/**
 * Uma preferência de visualização guardada por coleção.
 *
 * Eram cinco pares ler/gravar com o mesmo corpo — monta a chave, lê, tenta o `JSON.parse`,
 * confere a forma, devolve o padrão em qualquer tropeço. A fábrica escreve isso uma vez; o que
 * de fato varia entre elas é só a validação, que entra como parâmetro.
 *
 * **Toda leitura é defensiva e todo gravar engole a exceção.** Conteúdo velho, adulterado ou
 * `localStorage` indisponível não podem derrubar a tela — preferência de visualização não vale
 * interromper o uso da página. Quem lê recebe `null` e cai no próprio padrão.
 */
function preference<T>(name: string, parse: (raw: any) => T | null) {
    //O terceiro segmento é o RECORTE: sem ele a preferência é da coleção; com ele, de um
    //recorte dela — hoje o Kanban de um relacionamento, que mostra a mesma coleção de destino
    //vista de um item. Sem o recorte, esse quadro e a página da coleção dividiriam a mesma
    //configuração e se sobrescreveriam. Ausente não muda a chave, então nada do que já está
    //guardado se perde.
    const keyOf = (systemAlias: string, ideaAlias: string, scope?: string) =>
        `${name}:${systemAlias}/${ideaAlias}${scope ? `/${scope}` : ''}`
    return {
        read(systemAlias: string, ideaAlias: string, scope?: string): T | null {
            try {
                const raw = window.localStorage.getItem(keyOf(systemAlias, ideaAlias, scope))
                return raw ? parse(JSON.parse(raw)) : null
            } catch {
                return null
            }
        },
        write(systemAlias: string, ideaAlias: string, scope: string | undefined, value: T) {
            try {
                window.localStorage.setItem(keyOf(systemAlias, ideaAlias, scope), JSON.stringify(value))
            } catch {
                //idem
            }
        }
    }
}

/** Lista guardada: filtra item a item, para um item estranho não invalidar a escolha toda. */
function listOf<T>(isValid: (item: any) => boolean) {
    return (raw: any): T[] | null => Array.isArray(raw) ? raw.filter(isValid) : null
}

const aliasList = listOf<string>(item => typeof item === 'string')

/**
 * Os valores visíveis de cada dimensão do Kanban, por chave de dimensão.
 *
 * Antes era uma lista solta de ids de situação, e ela não serve mais: a primeira dimensão pode
 * ser uma seleção, e o id 3 de uma opção casaria por acaso com o id 3 de uma situação — o
 * quadro abriria com raias escondidas sem explicação. Guardado por dimensão, o que sobra de uma
 * dimensão que não está mais em uso é simplesmente ignorado.
 *
 * Reaproveita a chave antiga de propósito: o que está gravado lá é um array, não casa com esta
 * forma, e vira `null` — a escolha de raias se perde uma vez, como já aconteceu na migração de
 * alias para id, e a primeira gravação limpa o lixo.
 */
function visibleValuesOf(raw: any): VisibleValues | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
    const values: VisibleValues = {}
    Object.keys(raw).forEach(key => {
        if (Array.isArray(raw[key])) values[key] = raw[key].filter((id: any) => typeof id === 'number')
    })
    return values
}

function periodsOf(raw: any): KanbanPeriods | null {
    if (!raw || typeof raw !== 'object') return null
    const periods: KanbanPeriods = {}
    Object.keys(raw).forEach(id => {
        const period = asFinishedPeriod(raw[id])
        if (period) periods[id] = period
    })
    return periods
}

/**
 * O usuário desta sessão, **reativo** — e o resto do armazenamento não é.
 *
 * Só este precisa ser: ele decide o que a BARRA desenha, e a barra está sempre na tela. Sem
 * reatividade, nada redesenhava a casca quando o valor mudava: ao sair, o `v-if` do avatar já
 * tinha dito "sim" e sobrava uma bolinha vazia, porque o nome já não existia; ao entrar, a
 * bolinha não aparecia até alguém recarregar a página. O mesmo valia para o item de convites,
 * que depende do nível da conta.
 *
 * `shallowRef` e não `ref`: o que muda é o objeto inteiro, trocado de uma vez pelo `auth/sync`
 * ou pelo logout. Observar cada campo por dentro seria custo sem consumidor.
 *
 * O localStorage continua sendo onde ele **persiste**; isto aqui é a cópia que a tela enxerga, e
 * as duas são escritas pelo mesmo `set` logo abaixo. A leitura inicial acontece uma vez, no
 * carregamento do módulo.
 */
const currentUser = shallowRef<StoredUser | null>(readStoredUser())

function readStoredUser(): StoredUser | null {
    const raw = window.localStorage.getItem('user')
    return raw ? JSON.parse(raw) : null
}

const kanbanPeriodsPref = preference<KanbanPeriods>('kanban-periods', periodsOf)
const kanbanFieldsPref = preference<string[]>('kanban-fields', aliasList)
const kanbanValuesPref = preference<VisibleValues>('kanban-lanes', visibleValuesOf)
const kanbanDimensionsPref = preference<string[]>('kanban-dimensions', aliasList)
const listColumnsPref = preference<string[]>('list-columns', aliasList)
const relationshipViewPref = preference<RelationshipView>('relationship-view',
    raw => RELATIONSHIP_VIEWS.includes(raw as RelationshipView) ? raw as RelationshipView : null)

const storage = {
    set credentials(credentials: string | null) {
        if (credentials) window.localStorage.setItem('credentials', credentials)
        else window.localStorage.removeItem('credentials')
    },

    get credentials(): string | null {
        return window.localStorage.getItem('credentials')
    },

    set user(user: StoredUser | null) {
        if (user) window.localStorage.setItem('user', JSON.stringify(user))
        else window.localStorage.removeItem('user')
        currentUser.value = user
    },

    get user(): StoredUser | null {
        return currentUser.value
    },

    /** Prazo dos itens terminados, por id de raia terminal. Vazio, e não null: sem escolha
     *  guardada cada raia cai no prazo padrão, e quem monta esse mapa é o `periodsFor`. */
    kanbanPeriods(systemAlias: string, ideaAlias: string, scope?: string): KanbanPeriods {
        return kanbanPeriodsPref.read(systemAlias, ideaAlias, scope) || {}
    },

    setKanbanPeriods(systemAlias: string, ideaAlias: string, scope: string | undefined, periods: KanbanPeriods) {
        kanbanPeriodsPref.write(systemAlias, ideaAlias, scope, periods)
    },

    /**
     * Campos extras mostrados no card do Kanban. Devolve null quando nunca houve escolha, para
     * o chamador distinguir "não escolhi nada ainda" (que cai no padrão) de "escolhi nenhum
     * campo" (lista vazia, que deve ser respeitada). É o contrato de todas as preferências de
     * lista daqui.
     */
    kanbanFields(systemAlias: string, ideaAlias: string, scope?: string): string[] | null {
        return kanbanFieldsPref.read(systemAlias, ideaAlias, scope)
    },

    setKanbanFields(systemAlias: string, ideaAlias: string, scope: string | undefined, fields: string[]) {
        kanbanFieldsPref.write(systemAlias, ideaAlias, scope, fields)
    },

    /** Os valores visíveis de cada dimensão do Kanban — as raias da primeira, os blocos das
     *  demais. Ver `visibleValuesOf` para o porquê de ser por dimensão. */
    kanbanValues(systemAlias: string, ideaAlias: string, scope?: string): VisibleValues | null {
        return kanbanValuesPref.read(systemAlias, ideaAlias, scope)
    },

    setKanbanValues(systemAlias: string, ideaAlias: string, scope: string | undefined, values: VisibleValues) {
        kanbanValuesPref.write(systemAlias, ideaAlias, scope, values)
    },

    /** As dimensões do Kanban, na ordem escolhida: a primeira são as raias. */
    kanbanDimensions(systemAlias: string, ideaAlias: string, scope?: string): string[] | null {
        return kanbanDimensionsPref.read(systemAlias, ideaAlias, scope)
    },

    setKanbanDimensions(systemAlias: string, ideaAlias: string, scope: string | undefined, keys: string[]) {
        kanbanDimensionsPref.write(systemAlias, ideaAlias, scope, keys)
    },

    /** As colunas visíveis da listagem, por alias de propriedade. */
    listColumns(systemAlias: string, ideaAlias: string): string[] | null {
        return listColumnsPref.read(systemAlias, ideaAlias)
    },

    setListColumns(systemAlias: string, ideaAlias: string, columns: string[]) {
        listColumnsPref.write(systemAlias, ideaAlias, undefined, columns)
    },

    /**
     * A aba em que o bloco de um relacionamento foi deixado, na ficha do item.
     *
     * Pelo mesmo recorte das outras preferências do quadro: é por PROPRIEDADE, então abrir a
     * ficha de outro projeto mostra as tarefas do mesmo jeito. O segundo alias é o da coleção
     * DONA, que é onde a propriedade vive.
     */
    relationshipView(systemAlias: string, ideaAlias: string, scope: string): RelationshipView | null {
        return relationshipViewPref.read(systemAlias, ideaAlias, scope)
    },

    setRelationshipView(systemAlias: string, ideaAlias: string, scope: string, view: RelationshipView) {
        relationshipViewPref.write(systemAlias, ideaAlias, scope, view)
    },

    /**
     * A última visão usada nesta coleção — lista ou Kanban. É o que a URL da coleção consulta
     * para saber para onde mandar.
     *
     * Fora da fábrica de propósito: é o único que guarda texto cru e não JSON, e passá-lo pelo
     * `JSON.parse` estouraria em todo valor já gravado. Devolve null quando nunca houve escolha
     * e também quando o guardado não é mais uma das duas, para um valor antigo cair no padrão
     * em vez de virar navegação para lugar nenhum.
     */
    collectionView(systemAlias: string, ideaAlias: string): CollectionView | null {
        try {
            const raw = window.localStorage.getItem(collectionViewKey(systemAlias, ideaAlias))
            return COLLECTION_VIEWS.includes(raw as CollectionView) ? raw as CollectionView : null
        } catch {
            return null
        }
    },

    setCollectionView(systemAlias: string, ideaAlias: string, view: CollectionView) {
        try {
            window.localStorage.setItem(collectionViewKey(systemAlias, ideaAlias), view)
        } catch {
            //idem
        }
    },

    /**
     * Mostrar os ambientes encerrados na lista.
     *
     * Sem chave de ambiente: a lista é de todos eles, e é a única preferência do app que não
     * pertence a um. Falso por padrão — encerrar é para tirar da frente, e a lista voltar cheia
     * a cada visita desfaria o gesto.
     */
    showClosedWorkspaces(): boolean {
        try {
            return window.localStorage.getItem('ontos.showClosedWorkspaces') === 'true'
        } catch {
            return false
        }
    },

    setShowClosedWorkspaces(show: boolean) {
        try {
            window.localStorage.setItem('ontos.showClosedWorkspaces', String(show))
        } catch {
            //idem
        }
    }
}

export default storage
